// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Kickers — scoops, saucers, troughs, kickbacks (M1-18, PH-A24).
//
// A held ball is modelled as an **absence of simulation**, not as a very strong force. The
// ball is removed from the physics for the duration of the hold: it is not falling, not
// rolling, not resting against anything, and therefore cannot jitter, creep or escape. The
// alternative — a stiff spring holding it in a bowl — is the classic way to end up with a
// ball that hums quietly in a scoop for the rest of the game.
//
// Capture is speed-limited, because it has to be. A scoop that swallows anything entering
// its radius catches balls that a real one would spit straight back out across the
// playfield, and the resulting machine plays nothing like the one the author designed.

import type { Ball } from '../Ball'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_BALL_CAPTURED, EV_BALL_EJECTED, EV_SWITCH_CLOSED } from '../events'

export interface KickerConfig {
  readonly id: string
  readonly centre: readonly [number, number]
  readonly captureRadius: number
  /** Fastest ball this kicker can swallow, m/s. Above it the ball skips out. */
  readonly captureSpeed?: number
  /**
   * Steps to hold before ejecting. Negative holds indefinitely, until the rule engine calls
   * `eject()` — which is what a scoop under mode control actually does.
   */
  readonly holdSteps?: number
  /** Unit exit direction. */
  readonly exitDirection: readonly [number, number]
  readonly exitSpeed: number
  /** Steps after an eject during which this kicker will not recapture. */
  readonly rearmSteps?: number
  /** Height band in which a ball can drop in. */
  readonly zRange?: readonly [number, number]
}

export class Kicker extends Device {
  exitLayer = -1
  lockable = false
  physicalLocked = false
  lockHeld(): boolean {
    if (!this.lockable || this.heldBall < 0 || this.physicalLocked) return false
    this.physicalLocked = true
    this.releaseAtStep = -1
    return true
  }
  readonly kind = 'kicker'
  override readonly wantsObserve = true

  readonly cx: number
  readonly cy: number
  readonly captureRadius: number

  private readonly captureSpeed: number
  private readonly holdSteps: number
  private readonly exitX: number
  private readonly exitY: number
  private readonly exitSpeed: number
  private readonly rearmSteps: number
  private readonly zMin: number
  private readonly zMax: number

  /** Index of the ball held, or −1. */
  heldBall = -1
  private releaseAtStep = -1
  private rearmedAtStep = -1

  protected override ownState(): DeviceState {
    // `heldBall` and `releaseAtStep` together are "there is a ball in the scoop and it leaves at
    // step N". Dropping either resumes a game with a ball that never comes back out, or one that
    // is fired at the wrong moment — which is precisely the case M4-28a names.
    return {
      heldBall: this.heldBall,
      releaseAtStep: this.releaseAtStep,
      rearmedAtStep: this.rearmedAtStep,
      ...(this.lockable ? { physicalLocked: this.physicalLocked ? 1 : 0 } : {}),
    }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.heldBall = Number(state['heldBall'])
    this.releaseAtStep = Number(state['releaseAtStep'])
    this.rearmedAtStep = Number(state['rearmedAtStep'])
    this.physicalLocked = state['physicalLocked'] === 1
  }

  constructor(config: KickerConfig) {
    super(config.id)
    this.cx = config.centre[0]
    this.cy = config.centre[1]
    this.captureRadius = config.captureRadius
    this.setOrigin(this.cx, this.cy)
    this.captureSpeed = config.captureSpeed ?? 2.2
    this.holdSteps = config.holdSteps ?? 0

    const d = config.exitDirection
    const len = Math.sqrt(d[0] * d[0] + d[1] * d[1])
    if (len === 0) throw new Error(`Kicker ${config.id}: zero-length exitDirection`)
    this.exitX = d[0] / len
    this.exitY = d[1] / len
    this.exitSpeed = config.exitSpeed
    this.rearmSteps = config.rearmSteps ?? 120
    this.zMin = config.zRange ? config.zRange[0] : -Infinity
    this.zMax = config.zRange ? config.zRange[1] : Infinity
  }

  override observe(world: World, ball: Ball): void {
    if (this.heldBall >= 0 || ball.heldBy >= 0) return
    if (world.stepIndex < this.rearmedAtStep) return
    if (ball.z < this.zMin || ball.z > this.zMax) return

    const dx = ball.x - this.cx
    const dy = ball.y - this.cy
    if (dx * dx + dy * dy > this.captureRadius * this.captureRadius) return
    if (ball.speed() > this.captureSpeed) return

    this.capture(world, ball)
  }

  private capture(world: World, ball: Ball): void {
    const arrivalSpeed = ball.speed()

    ball.heldBy = this.index
    ball.x = this.cx
    ball.y = this.cy
    ball.vx = 0
    ball.vy = 0
    ball.vz = 0
    ball.wx = 0
    ball.wy = 0
    ball.wz = 0

    this.heldBall = ball.id
    this.releaseAtStep = this.holdSteps < 0 ? -1 : world.stepIndex + this.holdSteps

    world.events.emit(EV_BALL_CAPTURED, this.index, ball.id, world.stepIndex, arrivalSpeed)
    world.events.emit(EV_SWITCH_CLOSED, this.index, ball.id, world.stepIndex, arrivalSpeed)
  }

  override step(world: World, _dt: number): void {
    if (this.heldBall < 0 || this.releaseAtStep < 0) return
    if (world.stepIndex < this.releaseAtStep) return
    this.eject(world)
  }

  /**
   * Fire the coil now. Safe to call when nothing is held.
   *
   * `power` scales the exit speed, and exists for the plunger: a real one is pulled back a
   * variable distance, and that variable distance is the whole skill shot. A coil-driven
   * kicker or scoop has one strength and simply leaves it at the default.
   *
   * Out-of-range values are clamped rather than rejected. This is called from the input
   * layer, and an input layer that can strand the ball by asking for zero — or fling it past
   * the speed clamp by asking for ten — is a worse failure than a quietly bounded plunge.
   */
  eject(world: World, power = 1): void {
    const id = this.heldBall
    if (id < 0) return
    const ball = world.balls[id]
    if (ball === undefined) return

    let scale = power
    if (!(scale >= 0)) scale = 1 // NaN included: an unreadable control plunges full
    else if (scale > 1) scale = 1
    const speed = this.exitSpeed * scale

    ball.heldBy = -1
    if (this.exitLayer >= 0) ball.layer = this.exitLayer
    this.physicalLocked = false
    ball.vx = this.exitX * speed
    ball.vy = this.exitY * speed
    ball.vz = 0
    ball.stillSteps = 0

    this.heldBall = -1
    this.releaseAtStep = -1
    this.rearmedAtStep = world.stepIndex + this.rearmSteps

    world.events.emit(EV_BALL_EJECTED, this.index, id, world.stepIndex, speed)
  }

  override reset(): void {
    this.physicalLocked = false
    this.heldBall = -1
    this.releaseAtStep = -1
    this.rearmedAtStep = -1
  }
}
